import { Fragment, useMemo, useEffect, useRef, type ReactNode } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { MessageSquare } from 'lucide-react'
import { CommentEditorSubmitPayload } from '@/tiptap/tiptap-extensions/custom/comments/comment-editor/comment-editor'
import { CommentEditor } from '@/tiptap/tiptap-extensions/custom/comments/comment-editor/comment-editor'
import type { Comment } from '@/tiptap/tiptap-extensions/custom/comments/types'
import { useAtom } from 'jotai'
import { workspaceIdAtom } from '@/stores/workspaceStore'
import { trpc } from '@/lib/trpc'
import { cn } from '@/lib/utils'
import { ScrollArea } from '@deepguide-ai/ui/scroll-area'
import { CommentThread } from './CommentThread'
import LockCard from '@/pages/docs/components/LockCard'

interface CommentPanelProps {
  docId: string
  comments: Comment[]
  onResolve: (
    commentId: string,
    resolution: 'RESOLVED' | 'DISMISSED' | 'IMPLEMENTED'
  ) => void
  onDelete: (commentId: string) => void
  onReply: (
    parentId: string,
    content: string,
    extra?: { mentions?: string[]; richContent?: any }
  ) => void
  onEditComment?: (commentId: string, content: string, metadata?: any) => void
  onAddPageComment?: (
    content: string,
    pageSection?: string,
    extra?: { mentions?: string[]; richContent?: any }
  ) => void
  onCommentClick?: (comment: Comment) => void
  onApplySuggestion?: (commentId: string) => void
  onDismissCheck?: (commentId: string) => void
  className?: string
  onClose?: () => void
  // 外部激活的评论（用于滚动定位与高亮常亮）
  activeCommentId?: string | null
  // 激活来源：'editor' 时触发卡片闪烁；'panel' 不闪烁
  activeChangeSource?: 'panel' | 'editor'
  isLocked?: boolean
  showLockCardWhenLocked?: boolean
  onWithdraw?: () => void
  isWithdrawing?: boolean
  hideComposer?: boolean
  allowEdit?: boolean
  allowDelete?: boolean
  allowCopyLink?: boolean
  sortOrder?: 'asc' | 'desc'
  allowResolve?: boolean
  alwaysShowReplyEditor?: boolean
  threadsClassName?: string
  composerClassName?: string
  /** Optional action rendered as a sibling row directly under each TOP-LEVEL
   *  thread card (kept a sibling, not a wrapper, so `&>[data-comment-id]`
   *  styling from callers keeps matching). E.g. the auditor center's
   *  per-comment "Ask Vera". */
  renderThreadAction?: (comment: Comment) => ReactNode
}

export function CommentPanel({
  docId: _docId,
  comments,
  onResolve,
  onDelete,
  onReply,
  onEditComment,
  onAddPageComment,
  onCommentClick,
  onApplySuggestion,
  onDismissCheck,
  className,
  onClose: _onClose,
  activeCommentId,
  activeChangeSource,
  isLocked = false,
  showLockCardWhenLocked = true,
  onWithdraw,
  isWithdrawing = false,
  hideComposer = false,
  allowEdit = true,
  allowDelete = true,
  allowCopyLink = true,
  sortOrder = 'asc',
  allowResolve = true,
  alwaysShowReplyEditor = false,
  threadsClassName,
  composerClassName,
  renderThreadAction,
}: CommentPanelProps) {
  const [workspaceId] = useAtom(workspaceIdAtom)
  const membersQueryOptions = trpc.comments.getMentionableMembers.queryOptions()
  const { data: members = [] } = useQuery({
    ...(membersQueryOptions as any),
    enabled: !!workspaceId,
    staleTime: 5 * 60 * 1000,
  }) as {
    data: Array<{
      id: string
      name?: string | null
      email?: string | null
      image?: string | null
    }>
  }
  const panelRootRef = useRef<HTMLDivElement | null>(null)

  // Get all top-level comments sorted chronologically
  const sortedComments = useMemo(() => {
    return comments
      .filter((c) => !c.parentId)
      .sort(
        (a, b) =>
          sortOrder === 'desc'
            ? new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
            : new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      )
  }, [comments, sortOrder])

  const promoteMutation = useMutation(
    trpc.task.promoteFromComment.mutationOptions()
  )
  const handlePromoteToTask = (commentId: string) => {
    promoteMutation.mutate({ commentId } as any)
  }

  // 当外部指定激活评论时，滚动到该评论卡片并高亮
  useEffect(() => {
    if (!activeCommentId) return
    try {
      const root = panelRootRef.current || undefined
      const target = (root || document).querySelector(
        `[data-comment-id="${activeCommentId}"]`
      ) as HTMLElement | null
      if (target) {
        target.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
      }
    } catch (_e) {
      // ignore
    }
  }, [activeCommentId])

  return (
    <div className={cn('flex h-full min-h-0 flex-col gap-1 overflow-hidden', className)}>
      {sortedComments.length === 0 ? (
        <div className='flex min-h-0 flex-1 flex-col items-center justify-center px-6 py-10 text-center'>
          <MessageSquare
            className='h-10 w-10 text-text-disabled'
            strokeWidth={1.5}
          />
          <p className='mt-4 font-heading text-[13px] font-medium text-text-secondary'>
            No comments yet
          </p>
          <p className='mt-1 max-w-[260px] text-[12px] leading-relaxed text-text-subtle'>
            Comments on this document will appear here.
          </p>
        </div>
      ) : (
        <ScrollArea
          className='min-h-0 flex-1'
          viewClassName='[&>div]:!block'
        >
          <div
            ref={panelRootRef}
            className={cn('space-y-2 pb-6', threadsClassName)}
          >
            {sortedComments.map((comment) => {
              const isResolved =
                comment.commentStatus === 'RESOLVED' ||
                comment.commentStatus === 'DISMISSED' ||
                comment.commentStatus === 'IMPLEMENTED'

              return (
                <Fragment key={comment.id}>
                <CommentThread
                  comment={comment}
                  onResolve={onResolve}
                  onDelete={onDelete}
                  onReply={(pid, content, extra) => {
                    onReply(pid, content, extra)
                  }}
                  onEditComment={onEditComment}
                  onApplySuggestion={onApplySuggestion}
                  onDismissCheck={onDismissCheck}
                  onPromoteToTask={handlePromoteToTask}
                  isActive={comment.id === activeCommentId}
                  activeChangeSource={activeChangeSource}
                  members={members}
                  isMinimized={isResolved}
                  onClick={
                    isResolved || !onCommentClick
                      ? undefined
                      : () => onCommentClick(comment)
                  }
                  isLocked={isLocked}
                  allowEdit={allowEdit}
                  allowDelete={allowDelete}
                  allowCopyLink={allowCopyLink}
                  allowResolve={allowResolve}
                  alwaysShowReplyEditor={alwaysShowReplyEditor}
                />
                {!isResolved && renderThreadAction?.(comment)}
                </Fragment>
              )
            })}
          </div>
        </ScrollArea>
      )}
      {!hideComposer && (
        <div className={cn('shrink-0 bg-card pb-2', composerClassName)}>
          {/* 评论编辑器区域，当提交审核时，显示锁定状态 */}
          {isLocked ? (
            showLockCardWhenLocked ? (
              <LockCard onWithdraw={onWithdraw} isWithdrawing={isWithdrawing} />
            ) : null
          ) : (
            <CommentEditor
              onSubmit={(payload: CommentEditorSubmitPayload) => {
                onAddPageComment?.(payload.text, '', {
                  mentions: payload.mentions,
                  richContent: payload.json,
                })
              }}
              placeholder={'Add a comment…'}
              minHeightClass='min-h-[80px]'
              editorClassName={`max-h-32 shrink-0`}
              members={members}
            />
          )}
        </div>
      )}
    </div>
  )
}
